import React from "react";
import { formatOrderAmount, getOrderDate, getOrderStatus } from "./order";
import ImportResult from "./ImportResult";

const itemColumns = [
  ["orderLineID", "Line ID"],
  ["item_id", "Item ID"],
  ["ItemName", "ItemName"],
  ["quantity", "Quantity", "number"],
  ["price", "Price", "number"],
  ["total", "Total", "number"],
];

const PurchaseOrderDetails = ({ item, items = [], loading = false, error = "", importResult, importing, progress }) => {
  const availableKeys = new Set(items.flatMap(row => Object.keys(row || {})));
  const knownKeys = new Set(["company_id", "order_id", "itemName", "item_name", "originalPrice", "vendorCost", "checkedIn", "numReceived", "shippingCost", "discountMoneyValue", "discountPercentValue", "createTime", "timeStamp", ...itemColumns.map(([key]) => key)]);
  const columns = [
    ...itemColumns.filter(([key]) => key === "ItemName" || availableKeys.has(key)),
    ...Array.from(availableKeys).filter(key => !knownKeys.has(key)).map(key => [key, key]),
  ];
  const formatCell = (value, type) => {
    if (value == null) return "—";
    if (type === "number") return formatOrderAmount(value);
    if (type === "percent") return `${formatOrderAmount(Number(value) * 100)}%`;
    if (type === "date") return String(value).replace("T", " ");
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  };
  const fields = [
    ["Order ID", item.order_id],
    ["Reference", item.refNum || "—"],
    ["Order date", getOrderDate(item)],
    ["Status", getOrderStatus(item)],
    ["Vendor", item.vendorName || "—"],
    ["Vendor ID", item.vendorID],
    ["Shop", item.shopName || "—"],
    ["Received date", String(item.receivedDate || "").slice(0, 10) || "—"],
    ["Arrival date", String(item.arrivalDate || "").slice(0, 10) || "—"],
    ["Quantity", item.totalQuantity ?? 0],
    ["Subtotal", formatOrderAmount(item.subTotalCost)],
    ["Shipping", formatOrderAmount(item.shipCost)],
    ["Other cost", formatOrderAmount(item.otherCost)],
    ["Discount", formatOrderAmount(item.discount)],
    ["Total", `${formatOrderAmount(item.totalCost)} ${item.vendorCurrencyCode || ""}`.trim()],
    ["Created", item.createTime?.replace("T", " ") || "—"],
    ["Updated", item.timeStamp?.replace("T", " ") || "—"],
  ];
  return (
    <div style={{ padding: "1.5rem", display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 24 }}>
      <section style={{ flex: "0 1 360px", minWidth: 0, maxWidth: "100%" }}>
      <h2 style={{ margin: "0 0 16px" }}>Purchase order: {item.refNum || item.order_id}</h2>
      <ImportResult result={importResult} />
      <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "120px minmax(0, 1fr)", gap: "12px 16px" }}>
        {fields.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt style={{ color: "#64748b" }}>{label}</dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
      </section>
      <section style={{ flex: "0 1 calc(50% - 192px)", minWidth: "min(100%, 420px)", maxWidth: "100%" }}>
      <h3 style={{ margin: "0 0 16px" }}>Items</h3>
      {importing && <p role="status">Importing purchase orders... {progress?.processed} / {progress?.total}</p>}
      {loading ? <div role="status">Loading order items...</div>
        : error ? <div role="alert">{error}</div>
        : items.length === 0 ? <p>No items.</p>
        : (
          <div style={{ overflowX: "auto" }}>
            <table aria-label="Order items" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>{columns.map(([key, label, type]) => (
                  <th key={key} style={{ textAlign: type === "number" || type === "percent" ? "right" : "left", whiteSpace: "nowrap", padding: 8, background: "#f0f3f5" }}>{label}</th>
                ))}</tr>
              </thead>
              <tbody>{items.map((row, index) => (
                <tr key={`${row.company_id ?? item.company_id}:${row.order_id ?? item.order_id}:${row.orderLineID ?? index}`}>{columns.map(([key, label, type]) => (
                  <td key={key} style={{ textAlign: type === "number" || type === "percent" ? "right" : "left", whiteSpace: "nowrap", padding: 8, borderBottom: "1px solid #e5e7eb" }}>{formatCell(key === "ItemName" ? row.ItemName ?? row.itemName ?? row.item_name : row[key], type)}</td>
                ))}</tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PurchaseOrderDetails;
